import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { trainingModules } from './data/modules';
import './Statistics.css';

const Statistics = () => {
  const navigate = useNavigate();
  const [quizData, setQuizData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('detailed');
  const [timeFilter, setTimeFilter] = useState('all');
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [usersSearchTerm, setUsersSearchTerm] = useState('');
  const [usersRoleFilter, setUsersRoleFilter] = useState('all');
  const [usersStatusFilter, setUsersStatusFilter] = useState('all');

  const [currentPage, setCurrentPage] = useState(1);
  const [rankingsPage, setRankingsPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const userRole = auth.currentUser?.displayName;
  const isCEO = userRole === 'ceo' || userRole === 'manager';

  const calculatePerformanceScore = (user) => {
    const scoreWeight = 0.4;
    const completionWeight = 0.3;
    const modulesWeight = 0.3;
    
    const normalizedScore = user.averageScore / 100;
    const normalizedCompletion = user.completionRate / 100;
    const normalizedModules = Math.min(user.passedModules / 10, 1);
    
    return Math.round(
      (normalizedScore * scoreWeight + 
       normalizedCompletion * completionWeight + 
       normalizedModules * modulesWeight) * 100
    );
  };

  useEffect(() => {
    if (!isCEO) {
      navigate('/dashboard');
      return;
    }
    loadQuizData();
    loadUsersData();
  }, [isCEO, navigate]);

  const loadQuizData = async () => {
    try {
      const q = query(collection(db, 'userProgress'), orderBy('lastAttempt', 'desc'));
      const querySnapshot = await getDocs(q);
      const data = [];
      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        data.push({
          id: doc.id,
          ...docData,
          lastAttempt: docData.lastAttempt?.toDate(),
          completedAt: docData.completedAt?.toDate(),
          validUntil: docData.validUntil?.toDate(),
          passedDate: docData.passedDate?.toDate(),
          failedDate: docData.failedDate?.toDate()
        });
      });
      setQuizData(data);
    } catch (error) {
      console.error('Error loading quiz data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsersData = async () => {
    try {
      const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const data = [];
      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        data.push({
          id: doc.id,
          ...docData,
          createdAt: docData.createdAt?.toDate(),
          lastLogin: docData.lastLogin?.toDate()
        });
      });
      setUsersData(data);
    } catch (error) {
      console.error('Error loading users data:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'users', userId));
        setUsersData(usersData.filter(user => user.id !== userId));
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Failed to delete user. Please try again.');
      }
    }
  };

  const filterByTimePeriod = (data) => {
    const now = new Date();
    switch (timeFilter) {
      case 'today': {
        const start = new Date(now);
        start.setHours(0, 0, 0, 0);
        return data.filter(item => item.lastAttempt >= start);
      }
      case 'week': {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return data.filter(item => item.lastAttempt >= weekAgo);
      }
      case 'month': {
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return data.filter(item => item.lastAttempt >= monthAgo);
      }
      case 'quarter': {
        const quarterAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        return data.filter(item => item.lastAttempt >= quarterAgo);
      }
      case 'this-week': {
        // Monday to Sunday (calendar week)
        const day = now.getDay();
        const diff = (day === 0 ? 6 : day - 1);
        const monday = new Date(now);
        monday.setDate(now.getDate() - diff);
        monday.setHours(0, 0, 0, 0);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        sunday.setHours(23, 59, 59, 999);
        return data.filter(item => item.lastAttempt >= monday && item.lastAttempt <= sunday);
      }
      case 'this-month': {
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        firstDay.setHours(0, 0, 0, 0);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        lastDay.setHours(23, 59, 59, 999);
        return data.filter(item => item.lastAttempt >= firstDay && item.lastAttempt <= lastDay);
      }
      default:
        return data;
    }
  };

  const filteredUsers = useMemo(() => {
    let filtered = usersData;
    if (usersRoleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === usersRoleFilter);
    }
    if (usersStatusFilter !== 'all') {
      filtered = filtered.filter(user => user.status === usersStatusFilter);
    }
    if (usersSearchTerm) {
      const searchLower = usersSearchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.role?.toLowerCase().includes(searchLower)
      );
    }
    return filtered;
  }, [usersData, usersRoleFilter, usersStatusFilter, usersSearchTerm]);

  const userRankings = useMemo(() => {
    const userMap = {};
    const timeFilteredData = filterByTimePeriod(quizData);
    timeFilteredData.forEach(item => {
      const userId = item.userEmail || item.userId;
      if (!userId) return;
      if (!userMap[userId]) {
        userMap[userId] = {
          name: item.userName || 'Unknown User',
          email: item.userEmail || '',
          role: item.userRole || 'Unknown Role',
          totalModules: 0,
          passedModules: 0,
          totalScore: 0,
          attempts: 0,
          lastActive: item.lastAttempt,
          modules: []
        };
      }
      const user = userMap[userId];
      user.attempts++;
      user.totalScore += item.currentScore || 0;
      if (item.isPassed && !user.modules.includes(item.moduleId)) {
        user.modules.push(item.moduleId);
        user.passedModules = user.modules.length;
      }
      if (!user.modules.includes(item.moduleId)) {
        user.modules.push(item.moduleId);
        user.totalModules = user.modules.length;
      }
      if (item.lastAttempt > user.lastActive) {
        user.lastActive = item.lastAttempt;
      }
    });
    const rankings = Object.values(userMap).map(user => ({
      ...user,
      averageScore: user.attempts > 0 ? Math.round(user.totalScore / user.attempts) : 0,
      completionRate: user.totalModules > 0 ? Math.round((user.passedModules / user.totalModules) * 100) : 0,
      performanceScore: calculatePerformanceScore(user)
    }));
    return rankings.sort((a, b) => b.performanceScore - a.performanceScore);
  }, [quizData, timeFilter]);

  // ============================================================
  // INCENTIVE DATA - MONDAY TO FRIDAY WEEKS
  // CAP AT 5 VIDEOS PER MON-FRI WEEK, PENALTY PER WEEK
  // ============================================================
  const incentiveData = useMemo(() => {
    const WEEK_TARGET = 5;
    const rewardPerVideo = 1000;
    const penaltyPerMiss = 500;
    
    const periodData = filterByTimePeriod(quizData);
    const passedData = periodData.filter(item => item.isPassed === true);

    if (passedData.length === 0) return [];

    // Determine the period start and end from the timeFilter
    const now = new Date();
    let periodStart, periodEnd;
    switch (timeFilter) {
      case 'this-week': {
        const day = now.getDay();
        const diff = (day === 0 ? 6 : day - 1);
        periodStart = new Date(now);
        periodStart.setDate(now.getDate() - diff);
        periodStart.setHours(0, 0, 0, 0);
        periodEnd = new Date(periodStart);
        periodEnd.setDate(periodStart.getDate() + 6);
        periodEnd.setHours(23, 59, 59, 999);
        break;
      }
      case 'this-month': {
        periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
        periodStart.setHours(0, 0, 0, 0);
        periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        periodEnd.setHours(23, 59, 59, 999);
        break;
      }
      default: {
        const dates = passedData.map(d => d.lastAttempt).filter(Boolean);
        if (dates.length === 0) return [];
        periodStart = new Date(Math.min(...dates));
        periodEnd = new Date(Math.max(...dates));
        break;
      }
    }

    // Build Monday-Friday weeks within the period
    const weeks = [];
    let current = new Date(periodStart);
    // Move to Monday
    while (current.getDay() !== 1) {
      current.setDate(current.getDate() - 1);
    }
    while (current <= periodEnd) {
      const weekStart = new Date(current);
      const weekEnd = new Date(current);
      weekEnd.setDate(weekEnd.getDate() + 4); // Friday
      weekEnd.setHours(23, 59, 59, 999);
      
      // Clip to period boundaries
      const effectiveStart = weekStart < periodStart ? periodStart : weekStart;
      const effectiveEnd = weekEnd > periodEnd ? periodEnd : weekEnd;
      
      // Only add if there's at least 1 day in the week within the period
      if (effectiveStart <= effectiveEnd) {
        weeks.push({ start: effectiveStart, end: effectiveEnd });
      }
      // Move to next Monday
      current.setDate(current.getDate() + 7);
    }

    // Group by user and calculate weekly totals
    const userMap = {};

    for (const week of weeks) {
      // Get all passed videos in this week
      const weekVideos = passedData.filter(item => 
        item.lastAttempt >= week.start && item.lastAttempt <= week.end
      );

      // Group by user
      const weekUserMap = {};
      weekVideos.forEach(item => {
        const userId = item.userEmail || item.userId;
        if (!userId) return;
        if (!weekUserMap[userId]) {
          weekUserMap[userId] = [];
        }
        weekUserMap[userId].push(item);
      });

      // For each user in this week, count capped videos
      for (const [userId, videos] of Object.entries(weekUserMap)) {
        const uniqueModules = new Set(videos.map(v => v.moduleId));
        const count = uniqueModules.size;
        const capped = Math.min(count, WEEK_TARGET);
        const missed = Math.max(0, WEEK_TARGET - capped);
        const reward = capped * rewardPerVideo;
        const penalty = missed * penaltyPerMiss;

        if (!userMap[userId]) {
          // Find user info from first video
          const firstVideo = videos[0];
          userMap[userId] = {
            name: firstVideo.userName || 'Unknown',
            email: firstVideo.userEmail || '',
            role: firstVideo.userRole || 'Unknown',
            totalRaw: 0,
            totalCapped: 0,
            totalMissed: 0,
            totalReward: 0,
            totalPenalty: 0,
          };
        }

        userMap[userId].totalRaw += count;
        userMap[userId].totalCapped += capped;
        userMap[userId].totalMissed += missed;
        userMap[userId].totalReward += reward;
        userMap[userId].totalPenalty += penalty;
      }
    }

    // Convert to array and calculate totals
    const results = Object.values(userMap).map(user => ({
      ...user,
      passedCount: user.totalCapped,
      missed: user.totalMissed,
      reward: user.totalReward,
      penalty: user.totalPenalty,
      total: user.totalReward - user.totalPenalty,
    }));

    results.sort((a, b) => b.total - a.total);
    return results;
  }, [quizData, timeFilter]);

  const uniqueModules = ['all', ...new Set(quizData.map(item => item.moduleId))];
  const uniqueRoles = ['all', ...new Set(usersData.map(user => user.role).filter(Boolean))];

  const filteredData = useMemo(() => {
    let filtered = quizData;
    filtered = filterByTimePeriod(filtered);
    filtered = filtered.filter(item => {
      if (filter === 'passed' && !item.isPassed) return false;
      if (filter === 'failed' && item.isPassed) return false;
      if (moduleFilter !== 'all' && item.moduleId !== moduleFilter) return false;
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          item.userName?.toLowerCase().includes(searchLower) ||
          item.userEmail?.toLowerCase().includes(searchLower) ||
          item.moduleTitle?.toLowerCase().includes(searchLower) ||
          item.userRole?.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
    return filtered;
  }, [quizData, filter, moduleFilter, searchTerm, timeFilter]);

  const timeFilteredData = filterByTimePeriod(quizData);
  const totalAttempts = timeFilteredData.length;
  const passedAttempts = timeFilteredData.filter(item => item.isPassed).length;
  const failedAttempts = timeFilteredData.filter(item => !item.isPassed).length;
  const passRate = totalAttempts > 0 ? Math.round((passedAttempts / totalAttempts) * 100) : 0;
  const totalScore = timeFilteredData.reduce((sum, item) => sum + (item.currentScore || 0), 0);
  const averageScore = totalAttempts > 0 ? Math.round(totalScore / totalAttempts) : 0;
  const activeUsers = new Set(timeFilteredData.map(item => item.userEmail || item.userId)).size;
  const totalRegisteredUsers = usersData.length;

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const rankingsTotalPages = Math.ceil(userRankings.length / itemsPerPage);
  const rankingsIndexOfLastItem = rankingsPage * itemsPerPage;
  const rankingsIndexOfFirstItem = rankingsIndexOfLastItem - itemsPerPage;
  const currentRankings = userRankings.slice(rankingsIndexOfFirstItem, rankingsIndexOfLastItem);

  const usersTotalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const usersIndexOfLastItem = usersPage * itemsPerPage;
  const usersIndexOfFirstItem = usersIndexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(usersIndexOfFirstItem, usersIndexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const paginateRankings = (pageNumber) => setRankingsPage(pageNumber);
  const paginateUsers = (pageNumber) => setUsersPage(pageNumber);

  const getPageNumbers = (totalPages, currentPage) => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }
    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    });
    return rangeWithDots;
  };

  const handleBackToDashboard = () => {
    navigate('/welcome');
  };

  const handleExportCSV = () => {
    let headers, csvData;
    if (viewMode === 'ranking') {
      headers = ['Rank', 'Name', 'Email', 'Role', 'Performance Score', 'Avg Score', 'Modules Passed', 'Completion Rate', 'Last Active'];
      csvData = userRankings.map((user, index) => [
        index + 1,
        user.name,
        user.email,
        user.role,
        `${user.performanceScore}%`,
        `${user.averageScore}%`,
        user.passedModules,
        `${user.completionRate}%`,
        user.lastActive?.toLocaleDateString() || 'N/A'
      ]);
    } else if (viewMode === 'users') {
      headers = ['Name', 'Email', 'Role', 'Status', 'Created At', 'Last Login'];
      csvData = filteredUsers.map(user => [
        user.name || 'N/A',
        user.email || 'N/A',
        user.role || 'N/A',
        user.status || 'N/A',
        user.createdAt?.toLocaleDateString() || 'N/A',
        user.lastLogin?.toLocaleDateString() || 'N/A'
      ]);
    } else if (viewMode === 'incentive') {
      headers = ['Name', 'Email', 'Role', 'Videos Passed (Capped)', 'Missed (Penalty)', 'Reward (UGX)', 'Penalty (UGX)', 'Total Incentive (UGX)'];
      csvData = incentiveData.map(user => [
        user.name,
        user.email,
        user.role,
        user.passedCount,
        user.missed,
        user.reward,
        user.penalty,
        user.total
      ]);
    } else {
      headers = ['Name', 'Email', 'Role', 'Module', 'Department', 'Score', 'Status', 'Attempt #', 'Date'];
      csvData = filteredData.map(item => [
        item.userName || 'N/A',
        item.userEmail || 'N/A',
        item.userRole || 'N/A',
        item.moduleTitle || 'N/A',
        item.moduleDepartment || 'N/A',
        `${item.currentScore || 0}%`,
        item.isPassed ? 'Passed' : 'Failed',
        item.attemptCount || 1,
        item.lastAttempt?.toLocaleDateString() || 'N/A'
      ]);
    }
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `training-${viewMode}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const refreshData = () => {
    setLoading(true);
    setCurrentPage(1);
    setRankingsPage(1);
    setUsersPage(1);
    loadQuizData();
    loadUsersData();
  };

  useEffect(() => {
    setCurrentPage(1);
    setRankingsPage(1);
    setUsersPage(1);
  }, [filter, moduleFilter, searchTerm, timeFilter, viewMode, usersRoleFilter, usersStatusFilter, usersSearchTerm]);

  if (!isCEO) {
    return null;
  }

  if (loading) {
    return (
      <div className="statistics-container">
        <div className="statistics-header">
          <h1>Training Statistics</h1>
          <button onClick={handleBackToDashboard} className="back-btn">
            ← Back to Home
          </button>
        </div>
        <div className="loading-spinner">Loading statistics...</div>
      </div>
    );
  }

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === 'incentive') {
      setTimeFilter('this-month');
    }
  };

  const getPeriodLabel = () => {
    const now = new Date();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    switch (timeFilter) {
      case 'this-month':
        return `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
      case 'this-week': {
        const day = now.getDay();
        const diff = (day === 0 ? 6 : day - 1);
        const monday = new Date(now);
        monday.setDate(now.getDate() - diff);
        return `Week of ${monday.toLocaleDateString()}`;
      }
      default:
        return 'Selected Period';
    }
  };

  return (
    <div className="statistics-container">
      
      {/* ============================================================ */}
      {/* VIEW TOGGLE - VISIBLE AND WORKING */}
      {/* ============================================================ */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        background: 'maroon',
        padding: '16px 20px',
        borderRadius: '12px',
        border: '4px solid #000',
        marginBottom: '20px',
        width: '100%',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
      }}>
        <button 
          onClick={() => handleViewModeChange('detailed')}
          style={{
            padding: '10px 20px',
            background: viewMode === 'detailed' ? '#fff' : 'rgba(255,255,255,0.7)',
            border: '2px solid #333',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: viewMode === 'detailed' ? 'bold' : 'normal',
            color: '#000'
          }}
        >
          📋 Detailed View
        </button>
        <button 
          onClick={() => handleViewModeChange('ranking')}
          style={{
            padding: '10px 20px',
            background: viewMode === 'ranking' ? '#fff' : 'rgba(255,255,255,0.7)',
            border: '2px solid #333',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: viewMode === 'ranking' ? 'bold' : 'normal',
            color: '#000'
          }}
        >
          🏆 Rankings
        </button>
        <button 
          onClick={() => handleViewModeChange('summary')}
          style={{
            padding: '10px 20px',
            background: viewMode === 'summary' ? '#fff' : 'rgba(255,255,255,0.7)',
            border: '2px solid #333',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: viewMode === 'summary' ? 'bold' : 'normal',
            color: '#000'
          }}
        >
          📊 Summary
        </button>
        <button 
          onClick={() => handleViewModeChange('users')}
          style={{
            padding: '10px 20px',
            background: viewMode === 'users' ? '#fff' : 'rgba(255,255,255,0.7)',
            border: '2px solid #333',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: viewMode === 'users' ? 'bold' : 'normal',
            color: '#000'
          }}
        >
          👥 Users
        </button>
        <button 
          onClick={() => handleViewModeChange('incentive')}
          style={{
            padding: '10px 20px',
            background: viewMode === 'incentive' ? '#fff' : 'rgba(255,255,255,0.7)',
            border: '2px solid #333',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: viewMode === 'incentive' ? 'bold' : 'normal',
            color: '#000'
          }}
        >
          💰 Incentives
        </button>
      </div>

      {/* Header Section */}
      <div className="statistics-header">
        <div className="header-left">
          <h1>📊 Training Analytics Dashboard</h1>
          <p className="subtitle">CEO View - Performance Tracking & Rankings</p>
        </div>
        <div className="header-actions">
          <div className="items-per-page-selector">
            <label>Show: </label>
            <select 
              value={itemsPerPage} 
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
                setRankingsPage(1);
                setUsersPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span>entries</span>
          </div>
          
          <button onClick={handleExportCSV} className="export-btn">
            📥 Export {viewMode === 'ranking' ? 'Rankings' : viewMode === 'users' ? 'Users' : viewMode === 'incentive' ? 'Incentives' : 'Data'}
          </button>
          <button onClick={refreshData} className="refresh-btn">
            🔄 Refresh
          </button>
          <button onClick={handleBackToDashboard} className="back-btn">
            ← Dashboard
          </button>
        </div>
      </div>

      {/* Time Period Filter */}
      {viewMode !== 'users' && (
        <div className="time-period-filter">
          <div className="time-period-buttons">
            <button 
              className={`time-btn ${timeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setTimeFilter('all')}
            >
              All Time
            </button>
            <button 
              className={`time-btn ${timeFilter === 'this-month' ? 'active' : ''}`}
              onClick={() => setTimeFilter('this-month')}
            >
              📅 This Month
            </button>
            <button 
              className={`time-btn ${timeFilter === 'this-week' ? 'active' : ''}`}
              onClick={() => setTimeFilter('this-week')}
            >
              📅 This Week
            </button>
            <button 
              className={`time-btn ${timeFilter === 'quarter' ? 'active' : ''}`}
              onClick={() => setTimeFilter('quarter')}
            >
              Last Quarter
            </button>
            <button 
              className={`time-btn ${timeFilter === 'month' ? 'active' : ''}`}
              onClick={() => setTimeFilter('month')}
            >
              Last Month
            </button>
            <button 
              className={`time-btn ${timeFilter === 'week' ? 'active' : ''}`}
              onClick={() => setTimeFilter('week')}
            >
              Last Week
            </button>
            <button 
              className={`time-btn ${timeFilter === 'today' ? 'active' : ''}`}
              onClick={() => setTimeFilter('today')}
            >
              Today
            </button>
          </div>
        </div>
      )}

      {/* Statistics Summary Cards */}
      <div className="stats-summary">
        <div className="stat-card total">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <h3>{totalAttempts}</h3>
            <p>Total Attempts</p>
            <small>in selected period</small>
          </div>
        </div>
        
        <div className="stat-card users" onClick={() => handleViewModeChange('users')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>{totalRegisteredUsers}</h3>
            <p>Registered Users</p>
            <small>click to view all</small>
          </div>
        </div>
        
        <div className="stat-card active-users">
          <div className="stat-icon">✨</div>
          <div className="stat-content">
            <h3>{activeUsers}</h3>
            <p>Active Users</p>
            <small>in selected period</small>
          </div>
        </div>
        
        <div className="stat-card passed">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{passedAttempts}</h3>
            <p>Passed</p>
            <small>{passRate}% pass rate</small>
          </div>
        </div>
        
        <div className="stat-card average">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <h3>{averageScore}%</h3>
            <p>Avg Score</p>
            <small>across all attempts</small>
          </div>
        </div>
        
        <div className="stat-card top-performer">
          <div className="stat-icon">🏆</div>
          <div className="stat-content">
            <h4 style={{color:'black',fontSize:'24px'}}>{userRankings[0]?.name || 'N/A'}</h4>
            <p>Top Performer</p>
          </div>
        </div>
      </div>

      {/* ============================================================ */}
      {/* INCENTIVE VIEW - MONDAY TO FRIDAY WEEK */}
      {/* ============================================================ */}
      {viewMode === 'incentive' && (
        <div className="incentive-view">
          <div className="section-header">
            <div>
              <h2>💰 Weekly / Monthly Incentives</h2>
              <p className="subtitle" style={{ color: '#e17055', fontWeight: 'bold' }}>
                📅 Period: {getPeriodLabel()} — Rewards capped at 5 videos per week (Monday–Friday).
                <br />
                <span style={{ color: '#00b894', fontSize: '13px' }}>
                  ✅ Reward: UGX 1,000 per video (max 5 per Mon-Fri week) | Penalty: UGX 500 per missed video below weekly target
                </span>
              </p>
            </div>
          </div>

          <div className="stats-summary" style={{ marginBottom: '20px' }}>
            <div className="stat-card total">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <h3>UGX {incentiveData.reduce((sum, user) => sum + user.total, 0).toLocaleString()}</h3>
                <p>Total Payout</p>
                <small>for selected period</small>
              </div>
            </div>
            <div className="stat-card users">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <h3>{incentiveData.length}</h3>
                <p>Eligible Users</p>
                <small>passed at least 1 video</small>
              </div>
            </div>
            <div className="stat-card passed">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3>{incentiveData.reduce((sum, user) => sum + user.passedCount, 0)}</h3>
                <p>Total Videos Passed (capped)</p>
                <small>across all users</small>
              </div>
            </div>
          </div>

          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Videos Passed (Capped)</th>
                  <th>Missed (Penalty)</th>
                  <th>Reward (UGX)</th>
                  <th>Penalty (UGX)</th>
                  <th>Total Incentive (UGX)</th>
                </tr>
              </thead>
              <tbody>
                {incentiveData.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="no-results">
                      No incentive data available for this period. Only videos passed in {getPeriodLabel()} count, capped at 5 per Monday-Friday week.
                    </td>
                  </tr>
                ) : (
                  incentiveData.map((user, index) => (
                    <tr key={user.email} className={user.total > 0 ? 'passed-row' : 'failed-row'}>
                      <td style={{ fontWeight: 'bold', color: '#333' }}>{index + 1}</td>
                      <td style={{ color: 'black' }} className="user-name">{user.name}</td>
                      <td style={{ color: 'black' }} className="user-role">{user.role}</td>
                      <td style={{ color: 'black', fontWeight: 'bold' }}>{user.passedCount}</td>
                      <td style={{ color: 'black' }}>{user.missed > 0 ? `${user.missed} (x500)` : 'None'}</td>
                      <td style={{ color: 'green', fontWeight: 'bold' }}>UGX {user.reward.toLocaleString()}</td>
                      <td style={{ color: 'red', fontWeight: 'bold' }}>{user.penalty > 0 ? `- UGX ${user.penalty.toLocaleString()}` : 'UGX 0'}</td>
                      <td style={{ color: user.total > 0 ? '#2d7d46' : '#c0392b', fontWeight: 'bold', fontSize: '1.1em' }}>UGX {user.total.toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Users View */}
      {viewMode === 'users' && (
        <div className="users-view">
          <div className="section-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
            <div>
              <h2>👥 Registered Users</h2>
              <p className="subtitle">Total: {totalRegisteredUsers} users</p>
            </div>
            <button 
              onClick={() => handleViewModeChange('detailed')} 
              className="back-to-stats-btn"
              style={{
                padding: '10px 20px',
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#0056b3'}
              onMouseOut={(e) => e.target.style.background = '#007bff'}
            >
              ← Back to Statistics
            </button>
          </div>

          <div className="filters-section">
            <div className="filter-row">
              <div className="filter-group">
                <label>Role Filter:</label>
                <select value={usersRoleFilter} onChange={(e) => setUsersRoleFilter(e.target.value)}>
                  <option value="all">All Roles</option>
                  {uniqueRoles.filter(role => role !== 'all').map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Status Filter:</label>
                <select value={usersStatusFilter} onChange={(e) => setUsersStatusFilter(e.target.value)}>
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <div className="filter-group search-group">
                <label>Search:</label>
                <input
                  type="text"
                  placeholder="Search by name, email, or role..."
                  value={usersSearchTerm}
                  onChange={(e) => setUsersSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="filter-info">
              <div className="results-count">Showing {filteredUsers.length} users total</div>
            </div>
          </div>

          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="no-results">No users found</td>
                  </tr>
                ) : (
                  currentUsers.map((user) => (
                    <tr key={user.id}>
                      <td style={{color:'black', fontWeight:'500'}}>{user.name || 'N/A'}</td>
                      <td style={{color:'#0066cc'}}>{user.email || 'N/A'}</td>
                      <td><span className="role-badge">{user.role || 'N/A'}</span></td>
                      <td><span className={`status-badge ${user.status || 'inactive'}`}>{user.status || 'inactive'}</span></td>
                      <td style={{color:'black'}}>
                        {user.createdAt?.toLocaleDateString() || 'N/A'}
                        <br/><small>{user.createdAt?.toLocaleTimeString() || ''}</small>
                      </td>
                      <td style={{color:'black'}}>
                        {user.lastLogin?.toLocaleDateString() || 'Never'}
                        <br/><small>{user.lastLogin?.toLocaleTimeString() || ''}</small>
                      </td>
                      <td>
                        <button onClick={() => handleDeleteUser(user.id)} className="delete-user-btn" title="Delete User">🗑️</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {usersTotalPages > 1 && (
              <div className="pagination-controls">
                <div className="pagination-info">Showing {usersIndexOfFirstItem + 1} to {Math.min(usersIndexOfLastItem, filteredUsers.length)} of {filteredUsers.length} entries</div>
                <div className="pagination-buttons">
                  <button onClick={() => paginateUsers(usersPage - 1)} disabled={usersPage === 1} className="pagination-btn">← Previous</button>
                  <div className="page-numbers">
                    {getPageNumbers(usersTotalPages, usersPage).map((pageNumber, index) => (
                      pageNumber === '...' ? <span key={`ellipsis-${index}`} className="page-ellipsis">...</span> :
                      <button key={pageNumber} onClick={() => paginateUsers(pageNumber)} className={`page-number ${usersPage === pageNumber ? 'active' : ''}`}>{pageNumber}</button>
                    ))}
                  </div>
                  <button onClick={() => paginateUsers(usersPage + 1)} disabled={usersPage === usersTotalPages} className="pagination-btn">Next →</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rankings View */}
      {viewMode === 'ranking' && (
        <div className="rankings-view">
          <div className="section-header">
            <h2>🏆 Employee Performance Rankings</h2>
            <p className="subtitle">Ranked by overall performance score</p>
          </div>
          <div className="rankings-table-container">
            <table className="rankings-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Performance Score</th>
                  <th>Avg Score</th>
                  <th>Modules Passed</th>
                  <th>Completion Rate</th>
                  <th>Total Attempts</th>
                  <th>Last Active</th>
                </tr>
              </thead>
              <tbody>
                {currentRankings.map((user, index) => {
                  const globalIndex = (rankingsPage - 1) * itemsPerPage + index;
                  return (
                    <tr key={user.email} className={`ranking-row ${globalIndex < 3 ? 'top-three' : ''}`}>
                      <td className="rank-cell">
                        {globalIndex < 3 ? (
                          <span className={`rank-badge rank-${globalIndex + 1}`}>
                            {globalIndex === 0 ? '🥇' : globalIndex === 1 ? '🥈' : '🥉'}
                          </span>
                        ) : (
                          <span className="rank-number">#{globalIndex + 1}</span>
                        )}
                      </td>
                      <td className="ranking-name">{user.name}</td>
                      <td className="ranking-role">{user.role}</td>
                      <td className="ranking-score">
                        <div className="score-bar-container">
                          <div className="score-bar-fill" style={{ width: `${user.performanceScore}%` }} />
                          <span className="score-value">{user.performanceScore}%</span>
                        </div>
                      </td>
                      <td style={{color:'black'}} className="ranking-avg">{user.averageScore}%</td>
                      <td style={{color:'black'}} className="ranking-modules">{user.passedModules} / {user.totalModules}</td>
                      <td style={{color:'black'}} className="ranking-completion">{user.completionRate}%</td>
                      <td style={{color:'black'}} className="ranking-attempts">{user.attempts}</td>
                      <td className="ranking-date">{user.lastActive?.toLocaleDateString() || 'N/A'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rankingsTotalPages > 1 && (
              <div className="pagination-controls">
                <div className="pagination-info">Showing {rankingsIndexOfFirstItem + 1} to {Math.min(rankingsIndexOfLastItem, userRankings.length)} of {userRankings.length} entries</div>
                <div className="pagination-buttons">
                  <button onClick={() => paginateRankings(rankingsPage - 1)} disabled={rankingsPage === 1} className="pagination-btn">← Previous</button>
                  <div className="page-numbers">
                    {getPageNumbers(rankingsTotalPages, rankingsPage).map((pageNumber, index) => (
                      pageNumber === '...' ? <span key={`ellipsis-${index}`} className="page-ellipsis">...</span> :
                      <button key={pageNumber} onClick={() => paginateRankings(pageNumber)} className={`page-number ${rankingsPage === pageNumber ? 'active' : ''}`}>{pageNumber}</button>
                    ))}
                  </div>
                  <button onClick={() => paginateRankings(rankingsPage + 1)} disabled={rankingsPage === rankingsTotalPages} className="pagination-btn">Next →</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detailed View */}
      {viewMode === 'detailed' && (
        <>
          <div className="filters-section">
            <div className="filter-row">
              <div className="filter-group">
                <label>Status Filter:</label>
                <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                  <option value="all">All Results</option>
                  <option value="passed">Passed Only</option>
                  <option value="failed">Failed Only</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Module Filter:</label>
                <select value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}>
                  {uniqueModules.map(moduleId => (
                    <option key={moduleId} value={moduleId}>
                      {moduleId === 'all' ? 'All Modules' : trainingModules[moduleId]?.title || moduleId}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group search-group">
                <label>Search:</label>
                <input
                  type="text"
                  placeholder="Search by name, email, or module..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="filter-info">
              <div className="results-count">Showing {filteredData.length} results total</div>
              <div className="period-indicator">
                Period: {timeFilter === 'all' ? 'All Time' : 
                  timeFilter === 'quarter' ? 'Last 90 Days' :
                  timeFilter === 'month' ? 'Last 30 Days' :
                  timeFilter === 'week' ? 'Last 7 Days' : 
                  timeFilter === 'this-month' ? 'This Month' :
                  timeFilter === 'this-week' ? 'This Week' : 'Today'}
              </div>
            </div>
          </div>

          <div className="results-table-container">
            <div className="section-header">
              <h2>📋 Detailed Results</h2>
              <div className="table-actions">
                <button onClick={() => handleViewModeChange('ranking')} className="view-ranking-btn">🏆 View Rankings</button>
              </div>
            </div>
            <table className="results-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Module</th>
                  <th>Department</th>
                  <th>Score</th>
                  <th>Status</th>
                  <th>Attempt #</th>
                  <th>Date</th>
                  <th>Validity</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="no-results">No results found</td>
                  </tr>
                ) : (
                  currentItems.map((item, index) => (
                    <tr key={`${item.id}-${index}`} className={item.isPassed ? 'passed-row' : 'failed-row'}>
                      <td style={{color:'black'}} className="user-name">{item.userName || 'N/A'}</td>
                      <td style={{color:'maroon'}} className="user-email">{item.userEmail || 'N/A'}</td>
                      <td style={{color:'black'}} className="user-role">{item.userRole || 'N/A'}</td>
                      <td style={{color:'black'}} className="module-title">{item.moduleTitle || 'N/A'}</td>
                      <td style={{color:'black'}} className="module-dept">{item.moduleDepartment || 'N/A'}</td>
                      <td className={`score-cell ${item.currentScore >= 80 ? 'high-score' : 'low-score'}`}>{item.currentScore || 0}%</td>
                      <td><span className={`status-badge ${item.isPassed ? 'passed' : 'failed'}`}>{item.isPassed ? '✅ Passed' : '❌ Failed'}</span></td>
                      <td style={{color:'black'}} className="attempt-count">{item.attemptCount || 1}</td>
                      <td style={{color:'black'}} className="attempt-date">{item.lastAttempt?.toLocaleDateString() || 'N/A'}</td>
                      <td className="validity">
                        {item.isPassed && item.validUntil ? (
                          item.validUntil > new Date() ? (
                            <span className="valid">Valid until {item.validUntil.toLocaleDateString()}</span>
                          ) : (
                            <span className="expired">Expired</span>
                          )
                        ) : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="pagination-controls">
                <div className="pagination-info">Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length} entries</div>
                <div className="pagination-buttons">
                  <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="pagination-btn">← Previous</button>
                  <div className="page-numbers">
                    {getPageNumbers(totalPages, currentPage).map((pageNumber, index) => (
                      pageNumber === '...' ? <span key={`ellipsis-${index}`} className="page-ellipsis">...</span> :
                      <button key={pageNumber} onClick={() => paginate(pageNumber)} className={`page-number ${currentPage === pageNumber ? 'active' : ''}`}>{pageNumber}</button>
                    ))}
                  </div>
                  <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className="pagination-btn">Next →</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Module Performance Summary */}
      {(viewMode === 'detailed' || viewMode === 'summary') && (
        <div className="module-performance">
          <div className="section-header">
            <h2>📋 Module Performance Summary</h2>
            <p className="subtitle">Performance metrics for each training module</p>
          </div>
          <div className="module-grid">
            {Object.keys(trainingModules).map(moduleId => {
              const module = trainingModules[moduleId];
              const moduleAttempts = timeFilteredData.filter(item => item.moduleId === moduleId);
              const modulePassed = moduleAttempts.filter(item => item.isPassed).length;
              const moduleRate = moduleAttempts.length > 0 ? Math.round((modulePassed / moduleAttempts.length) * 100) : 0;
              const avgScore = moduleAttempts.length > 0 ? 
                Math.round(moduleAttempts.reduce((sum, item) => sum + (item.currentScore || 0), 0) / moduleAttempts.length) : 0;
              
              return (
                <div key={moduleId} className="module-performance-card">
                  <div className="module-card-header">
                    <h4>{module.title}</h4>
                    <span className="module-dept-badge">{module.department}</span>
                  </div>
                  <div className="module-stats">
                    <div className="module-stat">
                      <span className="stat-label">Attempts:</span>
                      <span className="stat-value">{moduleAttempts.length}</span>
                    </div>
                    <div className="module-stat">
                      <span className="stat-label">Pass Rate:</span>
                      <span className={`stat-value ${moduleRate >= 80 ? 'high' : moduleRate >= 60 ? 'medium' : 'low'}`}>{moduleRate}%</span>
                    </div>
                    <div className="module-stat">
                      <span className="stat-label">Avg Score:</span>
                      <span className="stat-value">{avgScore}%</span>
                    </div>
                    <div className="module-stat">
                      <span className="stat-label">Duration:</span>
                      <span className="stat-value">{module.duration} min</span>
                    </div>
                  </div>
                  <div className="module-progress-bar">
                    <div className="progress-fill" style={{ width: `${moduleRate}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary View */}
      {viewMode === 'summary' && (
        <div className="summary-view">
          <div className="summary-grid">
            <div className="summary-card top-performers">
              <h3>🏆 Top 5 Performers</h3>
              <div className="top-list">
                {userRankings.slice(0, 5).map((user, index) => (
                  <div key={user.email} className="top-performer-item">
                    <div className="performer-rank">#{index + 1}</div>
                    <div className="performer-info">
                      <div className="performer-name">{user.name}</div>
                      <div className="performer-role">{user.role}</div>
                    </div>
                    <div style={{color:'black'}} className="performer-score">{user.performanceScore}%</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="summary-card module-leaders">
              <h3>📊 Best Performing Modules</h3>
              <div className="module-leader-list">
                {Object.keys(trainingModules)
                  .map(moduleId => {
                    const module = trainingModules[moduleId];
                    const moduleAttempts = timeFilteredData.filter(item => item.moduleId === moduleId);
                    const modulePassed = moduleAttempts.filter(item => item.isPassed).length;
                    const moduleRate = moduleAttempts.length > 0 ? Math.round((modulePassed / moduleAttempts.length) * 100) : 0;
                    return { moduleId, title: module.title, rate: moduleRate, attempts: moduleAttempts.length };
                  })
                  .sort((a, b) => b.rate - a.rate)
                  .slice(0, 5)
                  .map((module, index) => (
                    <div key={module.moduleId} className="module-leader-item">
                      <div className="module-rank">#{index + 1}</div>
                      <div className="module-info">
                        <div className="module-title">{module.title}</div>
                        <div className="module-attempts">{module.attempts} attempts</div>
                      </div>
                      <div className="module-rate">{module.rate}%</div>
                    </div>
                  ))}
              </div>
            </div>
            <div className="summary-card trends-card">
              <h3>📈 Quick Stats</h3>
              <div className="trends-stats">
                <div className="trend-stat">
                  <div className="trend-label">Overall Pass Rate</div>
                  <div className="trend-value">{passRate}%</div>
                </div>
                <div className="trend-stat">
                  <div className="trend-label">Active Users</div>
                  <div className="trend-value">{activeUsers}</div>
                </div>
                <div className="trend-stat">
                  <div className="trend-label">Avg Score</div>
                  <div style={{color:'black'}} className="trend-value">{averageScore}%</div>
                </div>
                <div className="trend-stat">
                  <div className="trend-label">Top Score</div>
                  <div className="trend-value">{Math.max(...userRankings.map(u => u.averageScore), 0)}%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Statistics;